/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMapMarkerAlt, faUser, faPhone,
  faEnvelope, faLink, faUpload, faCheckCircle, 
  faInfoCircle, faCheck, faShieldAlt, faBuilding,
  faUsers, faBriefcase, faGlobe, faFileAlt,
  faCalendar, faTimes, faImage, faRocket,
  faIdCard, faHome, faChevronDown, faSearch, faHandsHelping, faPlus
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faFacebook, faTwitter, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { buildApiUrl } from '../config/api';
import { useAutoSave } from '../hooks/useAutoSave';
import axios from 'axios';
import PhoneInput from '../components/PhoneInput';
import '../styles/RecruiterRegistrationForm.css';
import logoImage from '../assets/FINAL LOGO AK.png';

const NGO_TYPES = [
  "National",
  "International",
  "Community-based"
];

const FOCUS_AREAS = [
  "Education & Training",
  "Health & Medical Services",
  "Environment & Conservation",
  "Women & Youth Empowerment",
  "Child Welfare & Protection",
  "Poverty Alleviation & Livelihoods",
  "Human Rights & Advocacy",
  "Agriculture & Food Security",
  "Water & Sanitation",
  "Community Development",
  "Other"
];

const COUNTIES = [
  "Nairobi", "Mombasa", "Kisumu", "Nakuru", "Kiambu", "Kakamega", "Machakos",
  "Kajiado", "Meru", "Kilifi", "Uasin Gishu", "Bungoma", "Nyeri", "Trans Nzoia", "Other"
];

const TEAM_SIZES = [
  "1-10 employees",
  "11-50 employees",
  "51-100 employees",
  "101-200 employees",
  "201-500 employees",
  "500+ employees"
];

const NGORegistrationForm = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [contactPersons, setContactPersons] = useState([{
    name: '',
    designation: '',
    email: '',
    phone: ''
  }]);
  const [acceptVolunteers, setAcceptVolunteers] = useState('');

  // Auto-save configuration
  const AUTOSAVE_KEY = `ngo_registration_${user?.userId || 'temp'}`;

  // Use auto-save hook
  const {
    formData,
    setFormData,
    isSaving,
    saveStatus,
    lastSaveTime,
    lastBackendSaveTime,
    forceSave,
    hasRecentSave,
    clearSavedData
  } = useAutoSave({
    // Organization Information
    ngoName: '',
    registrationNumber: '',
    yearEstablished: '',
    ngoType: '',
    focusArea: [],
    customFocusArea: '',
    about: '',
    teamSize: '',
    numberOfMembers: '',
    operatingRegions: '',
    
    // Social Media
    linkedin: '',
    facebook: '',
    twitter: '',
    instagram: '',
    
    // Contact Information
    address: '',
    county: '',
    city: '',
    postalCode: '',
    country: 'Kenya',
    phone: '',
    altPhone: '',
    email: '',
    website: '',
    
    // Volunteering
    acceptVolunteers: '',
    volunteerAreas: '',
    
    // Logo visibility
    showLogoOnWebsite: true,
    
    // Terms
    accuracy: false,
    agreeTerms: false
  },
  AUTOSAVE_KEY,
  1000,
  null,
  true
  );

  // Persist contact persons across refreshes
  const CONTACT_PERSONS_AUTOSAVE_KEY = useMemo(() => `${AUTOSAVE_KEY}_contact_persons`, [AUTOSAVE_KEY]);
  
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CONTACT_PERSONS_AUTOSAVE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setContactPersons(parsed);
        }
      }
    } catch (_) { /* ignore */ }
  }, [CONTACT_PERSONS_AUTOSAVE_KEY]);

  useEffect(() => {
    try {
      localStorage.setItem(CONTACT_PERSONS_AUTOSAVE_KEY, JSON.stringify(contactPersons));
    } catch (_) { /* ignore quota errors */ }
  }, [contactPersons, CONTACT_PERSONS_AUTOSAVE_KEY]);

  // Update logo preview when formData changes
  useEffect(() => {
    if (formData.ngoLogo && formData.ngoLogo !== logoPreview) {
      setLogoPreview(formData.ngoLogo);
    }
  }, [formData.ngoLogo]);

  // Update acceptVolunteers state when formData changes
  useEffect(() => {
    if (formData.acceptVolunteers) {
      setAcceptVolunteers(formData.acceptVolunteers);
    }
  }, [formData.acceptVolunteers]);

  const updateProgress = useCallback(() => {
    const requiredFields = [
      formData.ngoName, formData.registrationNumber, formData.yearEstablished,
      formData.ngoType, formData.about, formData.teamSize, formData.operatingRegions,
      formData.address, formData.county, formData.city, formData.country,
      formData.phone, formData.email, formData.website,
      formData.acceptVolunteers
    ];

    // Check contact persons
    const validContactPersons = contactPersons.filter(cp => 
      cp.name && cp.designation && cp.email && cp.phone
    ).length > 0;

    // Check volunteer areas if accepting volunteers
    const volunteerFieldsValid = formData.acceptVolunteers === 'no' || 
                                 (formData.acceptVolunteers === 'yes' && formData.volunteerAreas);

    const filledFields = requiredFields.filter(field => {
      return field && field.toString().trim() !== '';
    }).length;
    
    // Check focusArea separately since it's an array
    const hasFocusArea = Array.isArray(formData.focusArea) && formData.focusArea.length > 0;

    // Total: 14 required fields + 1 focusArea + 1 contact persons + 1 volunteer areas + 2 terms = 19 total
    const totalRequired = 19;
    let totalFilled = filledFields;
    
    if (hasFocusArea) totalFilled++;
    if (validContactPersons) totalFilled++;
    if (volunteerFieldsValid) totalFilled++;
    if (formData.accuracy) totalFilled++;
    if (formData.agreeTerms) totalFilled++;

    const progressPercent = Math.min((totalFilled / totalRequired) * 100, 100);
    setProgress(progressPercent);
  }, [formData, contactPersons]);

  useEffect(() => {
    updateProgress();
  }, [updateProgress]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Handle acceptVolunteers change
      if (name === 'acceptVolunteers') {
        setAcceptVolunteers(value);
        if (value === 'no') {
          setFormData(prev => ({ ...prev, volunteerAreas: '' }));
        }
      }
    }
    
    updateProgress();
  }, [updateProgress, setFormData]);

  const handleFocusAreaToggle = useCallback((area) => {
    setFormData(prev => {
      const currentAreas = prev.focusArea || [];
      const isSelected = currentAreas.includes(area);
      
      return {
        ...prev,
        focusArea: isSelected
          ? currentAreas.filter(a => a !== area)
          : [...currentAreas, area],
        // Clear custom focus area if "Other" is deselected
        customFocusArea: (area === 'Other' && !isSelected) ? '' : prev.customFocusArea
      };
    });
    updateProgress();
  }, [updateProgress, setFormData]);

  const handleContactPersonChange = (index, field, value) => {
    const updated = [...contactPersons];
    updated[index] = { ...updated[index], [field]: value };
    setContactPersons(updated);
    updateProgress();
  };

  const addContactPerson = () => {
    setContactPersons([...contactPersons, {
      name: '',
      designation: '',
      email: '',
      phone: ''
    }]);
  };

  const removeContactPerson = (index) => {
    if (contactPersons.length > 1) {
      const updated = contactPersons.filter((_, i) => i !== index);
      setContactPersons(updated);
      updateProgress();
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      if (!file.type.match(/^image\/(png|jpg|jpeg|svg)$/i)) {
        alert('Only PNG, JPG, and SVG images are allowed');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const logoData = e.target.result;
        setLogoPreview(logoData);
        setFormData(prev => ({
          ...prev,
          ngoLogo: logoData
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCompleteProfile = async () => {
    if (isLoading || isSaving) return;
    try {
      await forceSave();
    } catch (e) {
      console.error('Flush before complete profile failed:', e);
    }
    const formEl = document.getElementById('ngoForm');
    if (formEl) {
      if (typeof formEl.requestSubmit === 'function') {
        formEl.requestSubmit();
      } else {
        formEl.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  };

  const handleGoToDashboard = async () => {
    try {
      await forceSave();
    } catch (e) {
      console.error('Failed to flush form before dashboard:', e);
    }
    navigate('/ngo-dashboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitError('');

    // Validation
    if (!formData.accuracy || !formData.agreeTerms) {
      setSubmitError('Please accept both the accuracy certification and terms and conditions');
      setIsLoading(false);
      return;
    }

    if (!Array.isArray(formData.focusArea) || formData.focusArea.length === 0) {
      setSubmitError('Please select at least one focus area');
      setIsLoading(false);
      return;
    }

    // Validate custom focus area if "Other" is selected
    if (formData.focusArea.includes('Other') && (!formData.customFocusArea || formData.customFocusArea.trim() === '')) {
      setSubmitError('Please specify your custom focus area');
      setIsLoading(false);
      return;
    }

    const validContactPersons = contactPersons.filter(cp => 
      cp.name && cp.designation && cp.email && cp.phone
    );

    if (validContactPersons.length === 0) {
      setSubmitError('Please add at least one contact person with all required fields');
      setIsLoading(false);
      return;
    }

    if (formData.acceptVolunteers === 'yes' && !formData.volunteerAreas) {
      setSubmitError('Please specify volunteer areas if you accept volunteers');
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const submitData = new FormData();

      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (typeof formData[key] === 'boolean') {
          submitData.append(key, formData[key].toString());
        } else if (Array.isArray(formData[key])) {
          // Handle arrays (like focusArea) by JSON stringifying
          if (formData[key].length > 0) {
            submitData.append(key, JSON.stringify(formData[key]));
          }
        } else if (formData[key]) {
          submitData.append(key, formData[key]);
        }
      });

      // Add contact persons as JSON
      submitData.append('contactPersons', JSON.stringify(contactPersons));

      // Add logo file if uploaded
      if (logoFile) {
        submitData.append('ngoLogo', logoFile);
      }

      const response = await axios.post(
        buildApiUrl('/api/ngo/complete-registration'),
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        // Clear saved form data on successful submission
        clearSavedData();
        localStorage.removeItem(CONTACT_PERSONS_AUTOSAVE_KEY);
        
        // Update localStorage with profile completion status
        localStorage.setItem('profileCompleted', 'true');
        localStorage.setItem('hasCompletedProfile', 'true');
        
        // Update user context to mark profile as completed
        if (typeof updateUser === 'function') {
          updateUser({ 
            profileCompleted: true,
            hasCompletedProfile: true
          });
        }
        
        // Show success message
        alert('✅ NGO registration completed successfully! Redirecting to your dashboard...');
        
        // Force refresh of user data to ensure profile completion is detected
        setTimeout(() => {
          sessionStorage.clear();
          localStorage.setItem('profileCompleted', 'true');
          localStorage.setItem('hasCompletedProfile', 'true');
          window.location.href = '/ngo-dashboard';
        }, 1000);
      } else {
        setSubmitError(response.data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setSubmitError(error.response?.data?.message || 'Failed to complete registration. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="recruiter-details-comprehensive">
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .recruiter-details-comprehensive {
          max-width: 100% !important;
        }
        .recruiter-header {
          width: 100% !important;
          max-width: 100% !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        .recruiter-header .header-container {
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          padding: 12px 20px 12px 10px !important;
          max-width: 100% !important;
          margin: 0 !important;
          width: 100% !important;
        }
        .recruiter-header .header-left {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          gap: 12px !important;
          margin-right: auto !important;
          margin-left: 0 !important;
          padding-left: 0 !important;
          flex: 0 0 auto !important;
          order: 1 !important;
        }
        .recruiter-header .header-left .logo-icon {
          flex-shrink: 0 !important;
        }
        .recruiter-header .header-left .logo-text {
          flex-shrink: 0 !important;
        }
        .recruiter-header .header-right {
          margin-left: auto !important;
          flex: 0 0 auto !important;
          order: 2 !important;
        }
      `}</style>
      
      {/* Header */}
      <header className="recruiter-header">
        <div className="header-container">
          <div className="header-left" onClick={() => navigate('/ngo-dashboard')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="logo-icon">
              <img src={logoImage} alt="AksharJobs Logo" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
            </div>
            <div className="logo-text">
              <h3 className="logo-title" style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>AksharJobs</h3>
              <p className="logo-subtitle" style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>NGO Registration</p>
            </div>
          </div>
          <div className="header-right">
            <div className="header-controls" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div className="auto-save-status" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                {isSaving && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#f39c12' }}>
                    <div className="spinner" style={{ width: '12px', height: '12px', border: '2px solid #f39c12', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    <span>Auto-saving...</span>
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#27ae60' }}>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Auto-saved {lastSaveTime && `at ${lastSaveTime.toLocaleTimeString()}`}</span>
                  </div>
                )}
                {saveStatus === 'error' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#e74c3c' }}>
                    <FontAwesomeIcon icon={faTimes} />
                    <span>Save failed - data preserved locally</span>
                  </div>
                )}
                {saveStatus === 'loaded' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#3498db' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Previous data restored</span>
                  </div>
                )}
                {!isSaving && !saveStatus && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#6c757d' }}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Auto-save enabled</span>
                  </div>
                )}
              </div>
              <button
                className="complete-profile-btn"
                type="button"
                onClick={handleCompleteProfile}
                disabled={isSaving || isLoading}
                style={{
                  background: (isSaving || isLoading) ? '#bdc3c7' : '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: (isSaving || isLoading) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isSaving && !isLoading) e.target.style.background = '#2980b9';
                }}
                onMouseLeave={(e) => {
                  if (!isSaving && !isLoading) e.target.style.background = '#3498db';
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} /> {(isSaving || isLoading) ? 'Submitting...' : 'Complete profile'}
              </button>
              <button
                className="go-to-dashboard-btn"
                type="button"
                onClick={handleGoToDashboard}
                disabled={isSaving || isLoading}
                style={{
                  background: (isSaving || isLoading) ? '#bdc3c7' : '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: (isSaving || isLoading) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: '600',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isSaving && !isLoading) e.target.style.background = '#4b5563';
                }}
                onMouseLeave={(e) => {
                  if (!isSaving && !isLoading) e.target.style.background = '#6b7280';
                }}
              >
                <FontAwesomeIcon icon={faRocket} /> {isSaving ? 'Saving...' : 'Go to dashboard'}
              </button>
              <button
                className="clear-form-btn"
                type="button"
                onClick={() => {
                  if (window.confirm('Are you sure you want to clear all saved form data? This cannot be undone.')) {
                    clearSavedData();
                    localStorage.removeItem(CONTACT_PERSONS_AUTOSAVE_KEY);
                    window.location.reload();
                  }
                }}
                style={{
                  background: 'transparent',
                  border: '1px solid #e74c3c',
                  color: '#e74c3c',
                  padding: '8px 15px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  transition: 'all 0.3s ease'
                }}
              >
                Clear Form Data
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="recruiter-main">
        <div className="recruiter-container">
          <div className="recruiter-form-card">
            {/* Main Form */}
            <div className="recruiter-form-container">
              <form id="ngoForm" onSubmit={handleSubmit}>
                {/* Enhanced Progress Section */}
                <div className="progress-section-comprehensive" style={{
                  background: '#ffffff',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '30px',
                  color: '#000000',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '2px solid rgba(0, 0, 0, 0.1)'
                }}>
                  <div className="progress-header-comprehensive" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: '#000000' }}>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '10px', color: '#0d9488' }} />
                      Registration Progress
                    </h2>
                    <div style={{
                      background: '#f5f5f5',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '18px',
                      fontWeight: 'bold',
                      color: '#000000'
                    }}>
                      {Math.round(progress)}%
                    </div>
                  </div>
                  
                  <div className="progress-bar-comprehensive" style={{
                    background: '#f5f5f5',
                    height: '12px',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    marginBottom: '15px',
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }}>
                    <div 
                      className="progress-fill-comprehensive" 
                      style={{ 
                        width: `${progress}%`,
                        height: '100%',
                        background: '#0d9488',
                        borderRadius: '6px',
                        transition: 'width 0.5s ease-in-out',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        right: '0',
                        top: '0',
                        height: '100%',
                        width: '20px',
                        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 100%)',
                        borderRadius: '0 6px 6px 0'
                      }}></div>
                    </div>
                  </div>

                  {/* Progress Status Message */}
                  <div style={{
                    fontSize: '14px',
                    opacity: 0.9,
                    textAlign: 'center',
                    marginBottom: '10px',
                    color: '#000000'
                  }}>
                    {Math.round(progress) === 100 ? (
                      <span style={{ color: '#0d9488', fontWeight: '600' }}>
                        🎉 Profile Complete! Ready to submit.
                      </span>
                    ) : Math.round(progress) >= 80 ? (
                      <span style={{ color: '#000000' }}>
                        Almost there! Just a few more fields to complete.
                      </span>
                    ) : Math.round(progress) >= 50 ? (
                      <span style={{ color: '#000000' }}>
                        Good progress! Keep filling out the form.
                      </span>
                    ) : (
                      <span style={{ color: '#000000' }}>
                        Let's get started! Fill out your NGO information.
                      </span>
                    )}
                  </div>
                </div>

                {submitError && (
                  <div className="error-message" style={{ marginBottom: '20px', padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '6px', color: '#c33' }}>
                    <FontAwesomeIcon icon={faInfoCircle} /> {submitError}
                  </div>
                )}

                {/* Organization Information Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faHandsHelping} />
                    Organization Information
                  </h2>

                  {logoPreview && (
                    <div className="profile-photo-preview-comprehensive" style={{ display: 'block' }}>
                      <img src={logoPreview} alt="NGO Logo" />
                    </div>
                  )}

                  <div className="form-group-comprehensive">
                    <label className="file-upload-comprehensive">
                      <FontAwesomeIcon icon={faImage} />
                      <div className="file-upload-text-comprehensive">
                        <strong>Upload NGO Logo</strong>
                        <p>Click to browse or drag and drop (PNG, JPG, SVG, max 2MB)</p>
                      </div>
                      <input type="file" accept="image/*" onChange={handleLogoChange} />
                    </label>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>NGO Name <span className="required">*</span></label>
                    <input
                      type="text"
                      name="ngoName"
                      value={formData.ngoName}
                      onChange={handleInputChange}
                      placeholder="Enter NGO name"
                      required
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Registration Number <span className="required">*</span></label>
                      <input
                        type="text"
                        name="registrationNumber"
                        value={formData.registrationNumber}
                        onChange={handleInputChange}
                        placeholder="e.g., NGO/2024/001"
                        required
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Year of Establishment <span className="required">*</span></label>
                      <input
                        type="number"
                        name="yearEstablished"
                        value={formData.yearEstablished}
                        onChange={handleInputChange}
                        placeholder="YYYY"
                        required
                        min="1900"
                        max={new Date().getFullYear()}
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>NGO Type <span className="required">*</span></label>
                    <select
                      name="ngoType"
                      value={formData.ngoType}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select NGO type</option>
                      {NGO_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Focus Areas (Select multiple) <span className="required">*</span></label>
                    <div style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '12px',
                      background: '#ffffff',
                      maxHeight: '250px',
                      overflowY: 'auto'
                    }}>
                      {FOCUS_AREAS.map((area) => (
                        <label
                          key={area}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 12px',
                            marginBottom: '4px',
                            cursor: 'pointer',
                            borderRadius: '6px',
                            background: (formData.focusArea || []).includes(area) 
                              ? '#fef3c7' 
                              : 'transparent',
                            transition: 'all 0.2s',
                            border: (formData.focusArea || []).includes(area) 
                              ? '1px solid #fbbf24' 
                              : '1px solid transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = (formData.focusArea || []).includes(area) 
                              ? '#fef3c7' 
                              : '#f3f4f6';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = (formData.focusArea || []).includes(area) 
                              ? '#fef3c7' 
                              : 'transparent';
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={(formData.focusArea || []).includes(area)}
                            onChange={() => handleFocusAreaToggle(area)}
                            style={{
                              width: '18px',
                              height: '18px',
                              marginRight: '10px',
                              cursor: 'pointer',
                              accentColor: '#f97316'
                            }}
                          />
                          <span style={{
                            fontSize: '14px',
                            color: '#374151',
                            fontWeight: (formData.focusArea || []).includes(area) ? '600' : '400'
                          }}>
                            {area}
                          </span>
                        </label>
                      ))}
                    </div>
                    {(formData.focusArea || []).includes('Other') && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <label style={{
                          display: 'block',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px'
                        }}>
                          Please specify your focus area <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.customFocusArea || ''}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              customFocusArea: e.target.value
                            }));
                            updateProgress();
                          }}
                          placeholder="Enter your custom focus area"
                          required={(formData.focusArea || []).includes('Other')}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            outline: 'none',
                            transition: 'all 0.2s'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#f97316';
                            e.target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.1)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      </div>
                    )}
                    {(formData.focusArea || []).length > 0 && (
                      <div style={{
                        marginTop: '8px',
                        fontSize: '13px',
                        color: '#6b7280',
                        fontStyle: 'italic'
                      }}>
                        Selected: {(() => {
                          const areas = [...(formData.focusArea || [])];
                          // Replace "Other" with the custom text if it exists
                          const otherIndex = areas.indexOf('Other');
                          if (otherIndex !== -1 && formData.customFocusArea) {
                            areas[otherIndex] = formData.customFocusArea;
                          }
                          return areas.join(', ');
                        })()}
                      </div>
                    )}
                  </div>

                  <div className="form-group-comprehensive">
                    <label>About Your Organization <span className="required">*</span></label>
                    <textarea
                      name="about"
                      value={formData.about}
                      onChange={handleInputChange}
                      required
                      placeholder="Brief description of your NGO's mission and activities"
                      rows="4"
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Number of Employees <span className="required">*</span></label>
                      <select
                        name="teamSize"
                        value={formData.teamSize}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select team size</option>
                        {TEAM_SIZES.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Operating Regions <span className="required">*</span></label>
                      <textarea
                        name="operatingRegions"
                        value={formData.operatingRegions}
                        onChange={handleInputChange}
                        required
                        placeholder="List the counties or regions where your NGO operates (e.g., Nairobi, Mombasa, Kisumu)"
                        rows="2"
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Number of Members <span className="required">*</span></label>
                    <input
                      type="number"
                      name="numberOfMembers"
                      value={formData.numberOfMembers}
                      onChange={handleInputChange}
                      required
                      min="1"
                      placeholder="Enter number of members"
                    />
                  </div>
                </div>

                {/* Social Media & Online Presence Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faGlobe} />
                    Social Media & Online Presence
                  </h2>

                  <div className="form-group-comprehensive">
                    <label>
                      <FontAwesomeIcon icon={faLinkedin} style={{ color: '#0077B5', marginRight: '8px' }} />
                      LinkedIn Profile
                    </label>
                    <input
                      type="url"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={handleInputChange}
                      placeholder="https://www.linkedin.com/company/your-ngo"
                    />
                  </div>

                  <div className="form-group-comprehensive">
                    <label>
                      <FontAwesomeIcon icon={faFacebook} style={{ color: '#1877F2', marginRight: '8px' }} />
                      Facebook Page
                    </label>
                    <input
                      type="url"
                      name="facebook"
                      value={formData.facebook}
                      onChange={handleInputChange}
                      placeholder="https://www.facebook.com/your-ngo"
                    />
                  </div>

                  <div className="form-group-comprehensive">
                    <label>
                      <FontAwesomeIcon icon={faTwitter} style={{ color: '#1DA1F2', marginRight: '8px' }} />
                      Twitter/X Handle
                    </label>
                    <input
                      type="text"
                      name="twitter"
                      value={formData.twitter}
                      onChange={handleInputChange}
                      placeholder="@your_ngo"
                    />
                  </div>

                  <div className="form-group-comprehensive">
                    <label>
                      <FontAwesomeIcon icon={faInstagram} style={{ color: '#E4405F', marginRight: '8px' }} />
                      Instagram Handle
                    </label>
                    <input
                      type="text"
                      name="instagram"
                      value={formData.instagram}
                      onChange={handleInputChange}
                      placeholder="@your_ngo"
                    />
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faEnvelope} />
                    Contact Information
                  </h2>

                  <div className="form-group-comprehensive">
                    <label>Registered Address <span className="required">*</span></label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      placeholder="Complete registered address"
                      rows="3"
                    />
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>County <span className="required">*</span></label>
                      <select
                        name="county"
                        value={formData.county}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select county</option>
                        {COUNTIES.map(county => (
                          <option key={county} value={county}>{county}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Town/City <span className="required">*</span></label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        placeholder="Town or city name"
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Postal Code</label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        placeholder="Postal code"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Country <span className="required">*</span></label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                        readOnly
                        style={{ background: '#f5f5f5' }}
                      />
                    </div>
                  </div>

                  <div className="form-row-comprehensive">
                    <div className="form-group-comprehensive">
                      <label>Phone Number <span className="required">*</span></label>
                      <PhoneInput
                        value={formData.phone}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, phone: value }));
                          updateProgress();
                        }}
                        required
                        placeholder="+254 XXX XXX XXX"
                      />
                    </div>
                    <div className="form-group-comprehensive">
                      <label>Alternative Phone</label>
                      <PhoneInput
                        value={formData.altPhone}
                        onChange={(value) => {
                          setFormData(prev => ({ ...prev, altPhone: value }));
                        }}
                        placeholder="+254 XXX XXX XXX"
                      />
                    </div>
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Official Email <span className="required">*</span></label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="contact@ngo.org"
                    />
                  </div>

                  <div className="form-group-comprehensive">
                    <label>Website URL</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://www.example.com"
                    />
                  </div>
                </div>

                {/* Contact Person Details Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faUser} />
                    Contact Person Details
                  </h2>

                  {contactPersons.map((person, index) => (
                    <div key={index} style={{
                      padding: '20px',
                      background: '#f8f9fa',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      position: 'relative',
                      border: '1px solid #e9ecef'
                    }}>
                      {contactPersons.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeContactPerson(index)}
                          style={{
                            position: 'absolute',
                            top: '15px',
                            right: '15px',
                            background: '#e74c3c',
                            color: 'white',
                            border: 'none',
                            padding: '5px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} /> Remove
                        </button>
                      )}
                      <h3 style={{ fontSize: '16px', color: '#333', marginBottom: '15px' }}>
                        Contact Person {index + 1}
                      </h3>
                      
                      <div className="form-row-comprehensive">
                        <div className="form-group-comprehensive">
                          <label>Full Name <span className="required">*</span></label>
                          <input
                            type="text"
                            value={person.name}
                            onChange={(e) => handleContactPersonChange(index, 'name', e.target.value)}
                            required
                            placeholder="Full name"
                          />
                        </div>
                        <div className="form-group-comprehensive">
                          <label>Designation <span className="required">*</span></label>
                          <input
                            type="text"
                            value={person.designation}
                            onChange={(e) => handleContactPersonChange(index, 'designation', e.target.value)}
                            required
                            placeholder="e.g., Director, Coordinator"
                          />
                        </div>
                      </div>

                      <div className="form-row-comprehensive">
                        <div className="form-group-comprehensive">
                          <label>Contact Email <span className="required">*</span></label>
                          <input
                            type="email"
                            value={person.email}
                            onChange={(e) => handleContactPersonChange(index, 'email', e.target.value)}
                            required
                            placeholder="person@ngo.org"
                          />
                        </div>
                        <div className="form-group-comprehensive">
                          <label>Contact Phone <span className="required">*</span></label>
                          <PhoneInput
                            value={person.phone}
                            onChange={(value) => handleContactPersonChange(index, 'phone', value)}
                            required
                            placeholder="+254 XXX XXX XXX"
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addContactPerson}
                    style={{
                      background: '#0d9488',
                      color: 'white',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      marginTop: '15px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add Another Contact Person
                  </button>
                </div>

                {/* Volunteering Opportunities Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faUsers} />
                    Volunteering Opportunities
                  </h2>

                  <div className="form-group-comprehensive">
                    <label>Are you willing to accept volunteers? <span className="required">*</span></label>
                    <select
                      name="acceptVolunteers"
                      value={formData.acceptVolunteers}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select option</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>

                  {formData.acceptVolunteers === 'yes' && (
                    <div className="form-group-comprehensive">
                      <label>Specific Areas for Volunteers <span className="required">*</span></label>
                      <textarea
                        name="volunteerAreas"
                        value={formData.volunteerAreas}
                        onChange={handleInputChange}
                        required={formData.acceptVolunteers === 'yes'}
                        placeholder="Please specify the areas where you need volunteers (e.g., Education & Teaching, Healthcare Support, Community Outreach, Event Management, Fundraising, etc.)"
                        rows="3"
                      />
                    </div>
                  )}
                </div>

                {/* Terms & Agreements Section */}
                <div className="section-comprehensive">
                  <h2 className="section-title-comprehensive">
                    <FontAwesomeIcon icon={faShieldAlt} />
                    Terms & Agreements
                  </h2>

                  <div className="checkbox-group" style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '10px',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    marginBottom: '15px'
                  }}>
                    <input 
                      type="checkbox" 
                      id="accuracy" 
                      name="accuracy" 
                      required
                      checked={formData.accuracy}
                      onChange={handleInputChange}
                      style={{ 
                        marginTop: '3px',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <label htmlFor="accuracy" style={{ 
                      textAlign: 'left',
                      margin: 0,
                      cursor: 'pointer',
                      flex: 1
                    }}>
                      I certify that all information provided above is accurate and correct to the best of my knowledge <span className="required">*</span>
                    </label>
                  </div>

                  <div className="checkbox-group" style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '10px',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    marginBottom: '15px'
                  }}>
                    <input 
                      type="checkbox" 
                      id="agreeTerms" 
                      name="agreeTerms" 
                      required
                      checked={formData.agreeTerms}
                      onChange={handleInputChange}
                      style={{ 
                        marginTop: '3px',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <label htmlFor="agreeTerms" style={{ 
                      textAlign: 'left',
                      margin: 0,
                      cursor: 'pointer',
                      flex: 1
                    }}>
                      I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> of Akshar Jobs <span className="required">*</span>
                    </label>
                  </div>

                  {/* Admin Approval Message */}
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: '#e3f2fd',
                    border: '1px solid #90caf9',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <FontAwesomeIcon 
                      icon={faInfoCircle} 
                      style={{
                        color: '#1976d2',
                        fontSize: '18px',
                        marginTop: '2px',
                        flexShrink: 0
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1976d2',
                        marginBottom: '6px'
                      }}>
                        Account Approval Process
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#424242',
                        lineHeight: '1.6'
                      }}>
                        Complete your profile <strong>80%</strong>. If it is <strong>80% completed</strong>, it will be reviewed and then approved by our admin team within <strong>24 hours</strong>. You will receive an email notification once your account is approved. Please check your <strong>spam folder</strong> if you don't see the email in your inbox.
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Section */}
                <div className="submit-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  {submitError && (
                    <div className="error-message" style={{ marginBottom: '12px', color: '#e74c3c', textAlign: 'center' }}>
                      {submitError}
                    </div>
                  )}
                  <button type="submit" className="submit-btn" disabled={isLoading} style={{ margin: '0 auto' }}>
                    <FontAwesomeIcon icon={faCheckCircle} /> 
                    {isLoading ? 'Submitting...' : 'Submit Registration'}
                  </button>
                  <p style={{ marginTop: '8px', color: '#666', fontSize: '12px', textAlign: 'center' }}>
                    Your account will be reviewed and activated within 24-48 hours
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NGORegistrationForm;
